<template>
  <div class="filter-panel">
    <!-- AI-Powered Search Section -->
    <div class="filter-section">
      <div class="flex items-center gap-2 mb-3">
        <ThemeIcon icon="sparkles" type="icons" classes="w-4 h-4" />
        <span class="font-semibold">AI-Powered Search</span>
      </div>
      
      <AgsNaturalLanguageSearch
        :selected-variables="selectedAgsVariables"
        @variable-selected="onNaturalLanguageVariableSelected"
        @variable-removed="onNaturalLanguageVariableRemoved"
        @location-detected="onLocationDetected"
      />
    </div>

    <!-- Divider -->
    <div class="filter-divider">
      <span>OR</span>
    </div>

    <!-- Manual Selection Section -->
    <div class="filter-section">
      <div class="flex items-center gap-2 mb-3">
        <ThemeIcon icon="filter" type="icons" classes="w-4 h-4" />
        <span class="font-semibold">Manual Selection</span>
      </div>

      <!-- Category Dropdown -->
      <div class="mb-4">
        <label class="filter-label mb-2 flex items-center gap-2">
          <ThemeIcon icon="tag" type="icons" classes="w-4 h-4" />
          Dataset Category
        </label>
        <ThemedSelect
          v-model="selectedCategory"
          :options="categoryOptions"
          placeholder="Select Category"
          @input="onCategoryChange"
        />
      </div>

      <!-- Sub-Category Dropdown -->
      <div class="mb-4">
        <label class="filter-label mb-2 flex items-center gap-2">
          <ThemeIcon icon="plus" type="icons" classes="w-4 h-4" />
          Add Sub-Category
        </label>
        <ThemedSelect
          ref="subCategorySelect"
          v-model="selectedSubCategory"
          :options="subCategoryOptions"
          placeholder="Select sub-category to add..."
          :disabled="!selectedCategory || availableSubCategories.length === 0"          @input="onSubCategoryChange"
        />
      </div>
    </div>

    <!-- Active selections hint (shown when variables are selected) -->
    <div v-if="selectedAgsVariables.length > 0" class="filter-section filter-section--hint">
      <div class="active-hint">
        <ThemeIcon icon="filter" type="icons" classes="w-4 h-4" />
        <span>
          {{ selectedAgsVariables.length }} active filter{{ selectedAgsVariables.length !== 1 ? 's' : '' }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import ThemeIcon from '../ThemeIcon.vue';
import ThemedSelect from '../ThemedSelect.vue';
import AgsNaturalLanguageSearch from './AgsNaturalLanguageSearch.vue';
import cresiteiq2Api from '../../../cresiteiq2/services/cresiteiq2Api.js';

export default {
  name: 'FilterPanel',

  components: {
    ThemeIcon,
    ThemedSelect,
    AgsNaturalLanguageSearch
  },
  
  props: {
    selectedAgsVariables: {
      type: Array,
      default: () => []
    },
    selectedAgsCategory: {
      type: String,
      default: ''
    },
    comparisonEnabled: {
      type: Boolean,
      default: false
    },
    comparisonScope: {
      type: String,
      default: 'state'
    }
  },
  
  data() {
    return {
      selectedCategory: this.selectedAgsCategory || '',
      selectedSubCategory: '',
      agsOpacityValue: 70, // Default AGS opacity (10-100%)

      loadingCategories: false,
      agsCategories: [],
      allCategoryVariables: {},
      
      // Scale directions for each variable (path -> boolean)
      scaleDirections: {},
      
      // Variable weights (path -> decimal 0-1)
      variableWeights: {}
    };
  },
  
  mounted() {
    // Load categories and variables when component mounts
    this.loadAllCategoriesAndVariables();
  },
  
  watch: {
    // Watch for variable changes to reinitialize weights
    selectedAgsVariables: {
      handler(newVariables, oldVariables) {
        if (newVariables.length !== oldVariables.length) {
          this.initializeWeights();
        }
      },
      deep: true
    },

    // Sync when parent resets the category (e.g. Clear All)
    selectedAgsCategory(newVal) {
      this.selectedCategory = newVal;
      if (!newVal) {
        this.selectedSubCategory = '';
      }
    }
  },
  
  computed: {
    categoryOptions() {
      // Don't add placeholder here - ThemedSelect handles it via placeholder prop
      return this.agsCategories;
    },
    
    availableSubCategories() {
      if (!this.selectedCategory) return [];
      return this.allCategoryVariables[this.selectedCategory] || [];
    },
    
    subCategoryOptions() {
      const selectedPaths = new Set(this.selectedAgsVariables.map(v => v.path));
      return this.availableSubCategories.map(subCat => {
        const isSelected = selectedPaths.has(subCat.path);
        return {
          label: isSelected ? `✓ ${subCat.label}` : subCat.label,
          value: subCat.path,
          disabled: isSelected
        };
      });
    },
    
    // Check if multiple variables are selected (2+)
    isMultiVariable() {
      return this.selectedAgsVariables.length >= 2;
    },
    
    // Get opacity as percentage for display
    opacityPercent() {
      return Math.round(this.agsOpacityValue);
    },
    
    // Show comparison section when AGS data is selected
    shouldShowComparisonSection() {
      return this.selectedAgsVariables.length > 0;
    }
  },
  
  methods: {
    /**
     * Handle variable selected from natural language search
     */
    onNaturalLanguageVariableSelected(variable) {
      // Check if already selected
      const alreadySelected = this.selectedAgsVariables.some(v => v.path === variable.path);
      if (alreadySelected) {
        console.log('[FilterPanel] Variable already selected:', variable.label);
        return;
      }

      // Add to selected variables
      const updated = [...this.selectedAgsVariables, variable];
      this.$emit('ags-variables-updated', { variables: updated });

      console.log('[FilterPanel] Variable added from NL search:', variable.label);
    },

    /**
     * Handle variable removed from natural language search
     */
    onNaturalLanguageVariableRemoved(variableData) {
      // Handle both old format (string) and new format (object)
      const path = typeof variableData === 'string' ? variableData : variableData.path;
      const categoryId = typeof variableData === 'object' ? variableData.category_id : null;
      
      // If category_id is provided, match both path and category_id
      // Otherwise, just match path (backward compatibility)
      const updated = this.selectedAgsVariables.filter(v => {
        if (categoryId !== null) {
          return !(v.path === path && v.category_id === categoryId);
        }
        return v.path !== path;
      });
      
      this.$emit('ags-variables-updated', { variables: updated });

      console.log('[FilterPanel] Variable removed from NL search:', path);
    },

    /**
     * Handle location detected from natural language search
     */
    onLocationDetected(locationData) {
      console.log('[FilterPanel] Location detected:', locationData);
      // Emit to parent (MapPage) to handle map zoom
      this.$emit('location-detected', locationData);
    },

    onCategoryChange() {
      // Reset sub-category when category changes
      this.selectedSubCategory = '';
      // Persist category in parent so it survives panel mode switches
      this.$emit('ags-category-changed', this.selectedCategory);
      // Focus the sub-category select so the user knows what to do next
      this.$nextTick(() => {
        const el = this.$refs.subCategorySelect?.$el || this.$refs.subCategorySelect;
        if (el) el.focus();
      });
    },
    
    onSubCategoryChange() {
      if (!this.selectedSubCategory) return;
      
      // Find the selected sub-category details
      const subCat = this.availableSubCategories.find(sc => sc.path === this.selectedSubCategory);
      if (!subCat) return;
      
      // Check if already selected
      const alreadySelected = this.selectedAgsVariables.some(v => v.path === subCat.path);
      if (alreadySelected) {
        this.selectedSubCategory = '';
        return;
      }
      
      // Add to selected variables
      const updated = [...this.selectedAgsVariables, subCat];
      this.$emit('ags-variables-updated', { variables: updated });

      // Show toast
      this.showAddedToast(subCat.label);
      
      // Reset selection
      this.selectedSubCategory = '';
    },
    
    removeAgsVariable(variable) {
      const updated = this.selectedAgsVariables.filter(v => v.path !== variable.path);
      this.$emit('ags-variables-updated', { variables: updated });
    },
    
    clearAllFilters() {
      // Clear all selected variables
      this.$emit('ags-variables-updated', { variables: [] });
      
      // Reset category selections
      this.selectedCategory = '';
      this.selectedSubCategory = '';
      
      // Reset scale directions and weights
      this.scaleDirections = {};
      this.variableWeights = {};
    },
    
    onAgsOpacityChange() {
      // Convert percentage (10-100) to decimal (0.1-1.0)
      const opacity = this.agsOpacityValue / 100;
      
      this.$emit('opacity-changed', {
        type: 'ags',
        value: opacity
      });
    },
    

    
    formatFilterValue(variable) {
      // This would show the current filter value/threshold
      // For now, just show a percentage as in the screenshot
      return '% 50%';
    },
    
    /**
     * Get scale direction for a specific variable
     * @param {String} variablePath - Variable path
     * @returns {Boolean} True if higher is better, false if higher is worse
     */
    getScaleDirection(variablePath) {
      return this.scaleDirections[variablePath] ?? false;
    },
    
    /**
     * Toggle scale direction for a specific variable
     * @param {String} variablePath - Variable path
     */
    toggleVariableScaleDirection(variablePath) {
      this.$set(this.scaleDirections, variablePath, !this.getScaleDirection(variablePath));
      
      this.$emit('scale-direction-changed', {
        variablePath,
        higherIsBetter: this.scaleDirections[variablePath],
        isSingleVariable: !this.isMultiVariable
      });
    },
    
    /**
     * Get weight for a specific variable
     * @param {String} variablePath - Variable path
     * @returns {Number} Weight as percentage (0-100)
     */
    getVariableWeight(variablePath) {
      if (!this.isMultiVariable) return 100;
      
      // Initialize weights if not set
      if (Object.keys(this.variableWeights).length === 0) {
        this.initializeWeights();
      }
      
      return Math.round((this.variableWeights[variablePath] || 0) * 100);
    },
    
    /**
     * Initialize equal weights for all variables
     */
    initializeWeights() {
      const count = this.selectedAgsVariables.length;
      if (count === 0) return;
      
      const equalWeight = 1.0 / count;
      
      // Create new weights object for reactivity
      const newWeights = {};
      this.selectedAgsVariables.forEach(variable => {
        newWeights[variable.path] = equalWeight;
      });
      
      // Replace entire object to trigger reactivity
      this.variableWeights = newWeights;
      
      // Emit weights changed event
      this.$emit('weights-changed', this.variableWeights);
    },
    
    /**
     * Handle weight input change
     * @param {String} variablePath - Variable path
     * @param {String|Number} value - Input value (from ThemedInput)
     */
    onWeightChange(variablePath, value) {
      const percentage = parseFloat(value);
      if (isNaN(percentage) || percentage < 0) return;
      
      // Convert percentage to decimal (0-1), clamped to 0-100%
      const newWeight = Math.min(100, Math.max(0, percentage)) / 100;
      
      // Get all other variables (excluding the one being changed)
      const otherVariables = this.selectedAgsVariables.filter(v => v.path !== variablePath);
      
      if (otherVariables.length === 0) {
        // Only one variable, set to 100%
        this.$set(this.variableWeights, variablePath, 1.0);
      } else {
        // Calculate remaining weight to distribute among other variables
        const remainingWeight = 1.0 - newWeight;
        
        // Get current total weight of other variables
        const otherVariablesTotal = otherVariables.reduce((sum, v) => {
          return sum + (this.variableWeights[v.path] || 0);
        }, 0);
        
        // Update the changed variable's weight using Vue.set for reactivity
        this.$set(this.variableWeights, variablePath, newWeight);
        
        // Redistribute remaining weight proportionally among other variables
        if (otherVariablesTotal > 0) {
          // Proportional distribution based on current weights
          otherVariables.forEach(variable => {
            const currentWeight = this.variableWeights[variable.path] || 0;
            const proportion = currentWeight / otherVariablesTotal;
            this.$set(this.variableWeights, variable.path, remainingWeight * proportion);
          });
        } else {
          // Equal distribution if other variables have no weight
          const equalWeight = remainingWeight / otherVariables.length;
          otherVariables.forEach(variable => {
            this.$set(this.variableWeights, variable.path, equalWeight);
          });
        }
      }
      
      // Force Vue to update the view
      this.$forceUpdate();
      
      // Emit weight change event
      this.$emit('weights-changed', this.variableWeights);
    },
    
    showFilterInfo() {
      // Show information about active filters
      console.log('Show filter info');
    },

    showAddedToast(label) {
      this.$toast?.success(`Added: ${label}`, { timeout: 3000 });
    },
    
    /**
     * Handle comparison toggle change
     */
    onComparisonToggleChange(enabled) {
      // ThemedToggle emits boolean value directly
      this.$emit('comparison-toggled', {
        enabled: enabled,
        scope: this.comparisonScope
      });
    },
    
    /**
     * Handle comparison scope change
     */
    onScopeChange(value) {
      this.$emit('comparison-toggled', {
        enabled: this.comparisonEnabled,
        scope: value
      });
    },
    
    async loadAllCategoriesAndVariables() {
      this.loadingCategories = true;
      
      try {
        // Use the cresiteiq2Api service to fetch categories
        const response = await cresiteiq2Api.getAgsCategories();
        const categories = response.categories || [];
        
        // Transform categories for dropdown with source in label
        this.agsCategories = categories.map(category => ({
          value: category.category_name,
          label: `${category.display_name} (${category.source || 'Unknown'})`,
          source: category.source || 'Unknown'
        }));
        
        // Extract variables from each category
        this.allCategoryVariables = {};
        
        categories.forEach(category => {
          const variables = this.extractVariablesFromSchema(category.variable_schema || {});
          
          // Add category info to each variable
          const enrichedVariables = variables.map(variable => ({
            ...variable,
            category_name: category.category_name,
            category_id: category.id || category.category_name
          }));
          
          this.allCategoryVariables[category.category_name] = enrichedVariables;
        });
        
        console.log(`Loaded ${this.agsCategories.length} AGS categories for FilterPanel`);
        
      } catch (error) {
        console.error('Error loading AGS data:', error);
        this.agsCategories = [];
        this.allCategoryVariables = {};
      } finally {
        this.loadingCategories = false;
      }
    },
    
    extractVariablesFromSchema(schema) {
      const variables = [];
      
      if (schema.type === 'object' && schema.properties) {
        for (const [key, value] of Object.entries(schema.properties)) {
          if (value && typeof value === 'object' && value.type) {
            variables.push({
              path: key,
              label: value.description || key,
              type: value.type,
              description: value.description || null,
              format: this.inferFormat(value.type, key)
            });
          }
        }
      }
      
      return variables;
    },
    
    inferFormat(type, key) {
      // Infer format based on type and key name
      const keyLower = key.toLowerCase();
      
      if (keyLower.includes('income') || keyLower.includes('value') || keyLower.includes('rent')) {
        return 'currency';
      }
      
      if (keyLower.includes('rate') || keyLower.includes('percent')) {
        return 'percent';
      }
      
      return 'number';
    }
  }
};
</script>

<style lang="less">
@import '../../../../../less/theme/v1/components/filter-panel.less';
</style>
